import dayjs from 'dayjs';
import { get } from 'lodash';
import { useCallback, useReducer, useState } from 'react';

import * as Client from 'apis';
import envConfig from 'configs/envConfig';
import { useToast } from 'hooks/useToast';
import { useAuth } from 'providers/Auth';

import Context from './Context';
import { REDUCER_ACTIONS } from './define';
import reducer from './reducer';

const { SCHEDULE_EVENT_ID } = envConfig;

const initialState = {
    scheduleEvents: {},
    appointments: [],
    appointment: {},
    startDate: dayjs().toISOString(),
    endDate: dayjs().add(1, 's').toISOString()
};

const Provider = ({ children }) => {
    const [bookData, setBookData] = useState({});
    const [state, dispatch] = useReducer(reducer, initialState);
    const { scheduleEvents, appointments, appointment, startDate, endDate } = state;

    const { tpAuth = {}, liffAuth, refreshAuth } = useAuth();
    const { danger, info } = useToast();

    const bookAppointment = useCallback(
        async (detail) => {
            try {
                const res = await Client.bookAppointment(detail, liffAuth?.accessToken);

                if (res.err) {
                    const message = get(res, 'message', '系統忙碌中');
                    throw new Error(message);
                }

                info('預約成功');
                await refreshAuth();
                return { err: false, data: res.data };
            } catch (error) {
                console.log({ error });
                danger(error.message);
                return { err: true, message: error.message };
            }
        },
        [liffAuth?.accessToken]
    );

    const signupAppointment = useCallback(
        async (appointmentId, lineId, code) => {
            const res = await Client.signupAppointment(appointmentId, { lineId, code }, liffAuth?.accessToken);
            const isSuccess = !res.error && !res.err;
            if (isSuccess) await refreshAuth();
            return { err: res.error || res.err, message: get(res, 'message', '核銷失敗'), data: res.data ? res.data.data : null };
        },
        [liffAuth?.lineId, liffAuth?.accessToken]
    );

    const cancelAppointment = useCallback(
        async (appointmentId) => {
            const res = await Client.cancelAppointment(appointmentId, liffAuth?.accessToken);
            if (!res.error && !res.err) {
                await refreshAuth();
                return true;
            } else {
                const message = get(res, 'error.message', '取消失敗');
                danger(message);
                return false;
            }
        },
        [liffAuth?.accessToken]
    );

    const getAvailableTimes = useCallback(
        async (scheduleEventId, startDate, endDate, action) => {
            try {
                const res = await Client.getAvailableTimes(scheduleEventId, startDate, endDate, liffAuth?.accessToken);

                if (res.err) {
                    const message = get(res, 'message', '系統忙碌中');
                    throw new Error(message);
                }

                if (res && res.data) {
                    const type = action || REDUCER_ACTIONS.ADD_SCHEDULE_EVENT_AVAILABLE_TIMES;
                    dispatch({ type, payload: { ...res.data, startDate, endDate } });
                    return { err: false, data: res.data };
                }
            } catch (error) {
                console.log({ error });
                danger(error.message);
                return { err: true, message: error.message };
            }
        },
        [liffAuth?.accessToken]
    );

    const getScheduleEvent = useCallback(
        async (scheduleEventId, action) => {
            try {
                const res = await Client.getScheduleEvent(scheduleEventId, liffAuth?.accessToken);

                if (res.err) {
                    const message = get(res, 'message', '系統忙碌中');
                    throw new Error(message);
                }

                if (res && res.data) {
                    const type = action || REDUCER_ACTIONS.SET_SCHEDULE_EVENTS;
                    dispatch({ type, payload: res.data });
                    return { err: false, data: res.data };
                }
            } catch (error) {
                console.log({ error });
                danger(error.message);
                return { err: true, message: error.message };
            }
        },
        [liffAuth?.accessToken]
    );

    const getAppointmentsByEventIds = useCallback(
        async (action) => {
            try {
                const eventIds = [SCHEDULE_EVENT_ID];
                const res = await Client.getAppointmentsByEventIds(eventIds, liffAuth?.accessToken);

                if (res.err) {
                    const message = get(res, 'message', '系統忙碌中');
                    throw new Error(message);
                }

                if (res && res.data) {
                    const type = action || REDUCER_ACTIONS.ADD_APPOINTMENTS;
                    dispatch({ type, payload: res.data });
                    return { err: false, data: res.data };
                }
            } catch (error) {
                console.log({ error });
                danger(error.message);
                return { err: true, message: error.message };
            }
        },
        [liffAuth?.accessToken]
    );

    const getAppointmentById = useCallback(
        async (id, action) => {
            try {
                const res = await Client.getAppointmentById(id, liffAuth?.accessToken);

                if (res.err) {
                    const message = get(res, 'message', '系統忙碌中');
                    throw new Error(message);
                }

                if (res && res.data) {
                    const type = action || REDUCER_ACTIONS.SET_CURRENT_APPOINTMENT;
                    dispatch({ type, payload: res.data });
                    return { err: false, data: res.data };
                }
            } catch (error) {
                console.log({ error });
                danger(error.message);
                return { err: true, message: error.message };
            }
        },
        [liffAuth?.accessToken]
    );

    const props = {
        scheduleEvents,
        appointments,
        appointment,
        startDate,
        endDate,
        getAvailableTimes,
        bookAppointment,
        signupAppointment,
        getScheduleEvent,
        cancelAppointment,
        getAppointmentsByEventIds,
        getAppointmentById,
        bookData,
        setBookData
    };

    return <Context.Provider value={{ ...props }}>{children}</Context.Provider>;
};

export default Provider;
